import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { 
  Users, Stethoscope, MessageSquare, UserPlus, LogOut, CalendarDays, Heart, Trophy
} from "lucide-react";
import { PlateMindLogo } from "@/components/PlateMindLogo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LogoutConfirmDialog } from "@/components/ConfirmDialogs";
import LicensePurchaseModal from "@/components/LicensePurchaseModal";

const DietitianLayout = () => {
  const { user, token, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ patientCount: 0 });
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);

  // Only private practice physicians (no partner_id) can buy licenses
  const isPrivatePractice = user?.is_private_physician || !user?.partner_id;

  useEffect(() => {
    const handler = () => setShowLicenseModal(true);
    window.addEventListener('openLicensePurchase', handler);
    return () => window.removeEventListener('openLicensePurchase', handler);
  }, []);

  useEffect(() => {
    // Redirect if not dietitian or admin
    if (user && user.role !== "dietitian" && user.role !== "admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/dietitian/patients`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        if (response.ok) {
          const data = await response.json();
          setStats({ patientCount: data.patients?.length || 0 });
        }
      } catch (error) {
        console.error("Failed to fetch dietitian stats:", error);
      }
    };
    
    const fetchLeadsCount = async () => {
      try {
        const response = await fetch(`${API}/directory/my-leads?status=new`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        if (response.ok) {
          const data = await response.json();
          setNewLeadsCount(data.leads?.length || 0);
        }
      } catch (error) {
        // Leads endpoint might not exist for all dietitians
        console.log("No leads data available");
      }
    };
    
    if (token) {
      fetchStats();
      fetchLeadsCount();
    }
  }, [token]);

  const tabs = [
    { path: "/physician", label: t('physician.my_patients'), icon: Users, exact: true },
    { path: "/physician/weekly-summaries", label: t('physician.weekly_summaries'), icon: CalendarDays },
    { path: "/physician/leads", label: t('physician.leads'), icon: UserPlus, badge: newLeadsCount },
    { path: "/physician/messages", label: t('physician.message'), icon: MessageSquare },
    { path: "/physician/wellness", label: "Wellness", icon: Heart },
    { path: "/physician/leaderboard", label: "Leaderboard", icon: Trophy },
  ];

  const isActive = (tab) => {
    if (tab.exact) {
      return location.pathname === tab.path;
    }
    return location.pathname.startsWith(tab.path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-orange-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-blue-600" />
              <span className="font-bold text-secondary text-sm sm:text-base">{t('physician.dashboard')}</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <LanguageSwitcher variant="compact" />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-secondary">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{stats.patientCount} {t('physician.my_patients').toLowerCase()}</p>
              </div>
              <PlateMindLogo className="w-8 h-8 sm:w-10 sm:h-10" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLogoutDialog(true)}
                className="text-muted-foreground hover:text-red-500"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <nav className="flex gap-0.5 sm:gap-1 overflow-x-auto" style={{WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive(tab)
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-muted-foreground hover:text-secondary"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                  <span className={`${isActive(tab) ? "" : "hidden sm:inline"}`}>{tab.label}</span>
                  {tab.badge > 0 && (
                    <Badge className="bg-blue-500 text-white text-[10px] sm:text-xs h-4 sm:h-5 px-1 sm:px-1.5">
                      {tab.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <Outlet />
      </main>

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={() => {
          logout();
          navigate("/login?type=professional");
        }}
      />

      {/* License Purchase Modal (private practice only) */}
      {isPrivatePractice && (
        <LicensePurchaseModal open={showLicenseModal} onOpenChange={setShowLicenseModal} />
      )}
    </div>
  );
};

export default DietitianLayout;
